import type { ReactNode } from "react";
import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, markerShape, px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  type CategoryLabelLayout,
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  heightForLabels,
  layoutCategoryLabels,
  layoutLegend,
  type Rect,
  rotatedOverflow,
} from "@/lib/tool-kit/charts/layout";
import {
  isolatedPoints,
  linePath,
  looksLikeYears,
  type Point,
} from "@/lib/tool-kit/charts/lines";
import {
  bandScale,
  extent,
  linearScale,
  niceTicks,
} from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A line graph: one or more series over x categories (evenly spaced) or numeric x
 * (spaced to scale). Lines are straight between observations and break at a missing
 * value; markers differ in shape by series so the graph survives grayscale printing.
 */

export interface LineRow {
  /** The x value as written — the category label. */
  label: string;
  /** The x value as a number, for a numeric axis. */
  x: number | null;
  /** One y value per series; null leaves a gap. */
  values: (number | null)[];
}

/** Room between the plot's edge and the outermost points, so none sits on an axis. */
const INSET = 10;

interface XAxis {
  position: (index: number) => number;
  draw: ReactNode;
  labelHeight: number;
  /** The category labels' layout, and the first slot's distance from the plot edge. */
  labels: CategoryLabelLayout | null;
  firstCenter: number;
}

/** The x axis: evenly spaced category slots, or a numeric scale. */
function xAxisFor(
  rows: LineRow[],
  numeric: boolean,
  plot: Rect,
  apa: boolean,
  fmt: NumberFormat
): XAxis {
  if (!numeric) {
    const band = bandScale(rows.length, [plot.x, plot.x + plot.width], {
      paddingInner: 0,
      paddingOuter: 0,
    });
    const labels = layoutCategoryLabels(
      rows.map((row) => row.label),
      band.step
    );
    const positions = rows.map((_, index) => band.center(index));
    return {
      position: (index) => band.center(index),
      labelHeight: labels.height,
      labels,
      firstCenter: band.step / 2,
      draw: (
        <CategoryAxis
          apa={apa}
          axisLine
          labels={labels}
          orient="bottom"
          plot={plot}
          positions={positions}
          ticks
        />
      ),
    };
  }
  const xs = rows.map((row) => row.x ?? 0);
  const [low, high] = extent(xs) ?? [0, 1];
  const distinct = [...new Set(xs)].sort((a, b) => a - b);
  // A short run of whole numbers (weeks, grades, years) is labelled value by value.
  const ticks =
    xs.every(Number.isInteger) && distinct.length <= 12
      ? distinct
      : niceTicks(low, high, { exact: true, count: 6 }).ticks;
  const scale = linearScale(
    [low, high],
    [plot.x + INSET, plot.x + plot.width - INSET]
  );
  // Years read 2024, not 2,024.
  const grouping = !looksLikeYears(xs);
  const format = (value: number) =>
    fmt(value, { maximumFractionDigits: 4, useGrouping: grouping });
  return {
    position: (index) => scale(xs[index]),
    labelHeight: CHART_FONT_SIZE.tick,
    labels: null,
    firstCenter: 0,
    draw: (
      <ValueAxis
        apa={apa}
        format={format}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={scale}
        ticks={ticks}
      />
    ),
  };
}

function SeriesLine({
  rows,
  series,
  points,
  color,
  markers,
  valueLabels,
  fmt,
}: {
  rows: LineRow[];
  series: number;
  points: (Point | null)[];
  color: string;
  markers: boolean;
  valueLabels: boolean;
  fmt: NumberFormat;
}) {
  // Without markers, a point with no neighbour would be invisible: mark it anyway.
  const marked = markers
    ? points.map((_, index) => index)
    : isolatedPoints(points);
  return (
    <g>
      <path
        d={linePath(points)}
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
      {marked.map((index) => {
        const point = points[index];
        return point ? (
          <Marker
            fill={color}
            key={index}
            shape={markerShape(series)}
            x={point[0]}
            y={point[1]}
          />
        ) : null;
      })}
      {valueLabels
        ? points.map((point, index) =>
            point ? (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.value}
                // biome-ignore lint/suspicious/noArrayIndexKey: one label per point, in x order
                key={index}
                textAnchor="middle"
                x={px(point[0])}
                y={px(point[1] - 9)}
              >
                {valueText(fmt, rows[index].values[series] ?? 0)}
              </text>
            ) : null
          )
        : null}
    </g>
  );
}

export function LineChart({
  id,
  rows,
  names,
  numeric,
  markers,
  zero,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  /** In x order. */
  rows: LineRow[];
  /** Series names; more than one draws a legend. */
  names: string[];
  /** Space x to scale (every row needs `x`) rather than evenly. */
  numeric: boolean;
  markers: boolean;
  /** Start the y-axis at zero. */
  zero: boolean;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const [low, high] = extent(rows.flatMap((row) => row.values)) ?? [0, 1];
  const axis = niceTicks(low, high, { includeZero: zero });
  const tick = tickFormat(fmt, axis.decimals);
  const legend =
    names.length > 1
      ? layoutLegend(names, CHART_WIDTH - 2 * CHART_PADDING)
      : null;
  const base = {
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    legendHeight: legend?.height ?? 0,
    leftLabelWidth: maxTextWidth(axis.ticks.map(tick), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    topPadding: style.valueLabels ? 18 : 10,
  };
  const probe = cartesianFrame({ ...base, bottomLabelHeight: 0 });
  const probeAxis = xAxisFor(rows, numeric, probe.plot, style.apa, fmt);
  const sized = {
    ...base,
    height: heightForLabels(probeAxis.labelHeight),
    bottomLabelHeight: probeAxis.labelHeight,
  };
  const overflow = probeAxis.labels
    ? rotatedOverflow(
        cartesianFrame(sized),
        probeAxis.labels,
        probeAxis.firstCenter
      )
    : 0;
  const frame = cartesianFrame({
    ...sized,
    leftLabelWidth: sized.leftLabelWidth + overflow,
  });
  const { plot } = frame;
  const xAxis = xAxisFor(rows, numeric, plot, style.apa, fmt);
  // A zero baseline sits on the axis; any other minimum gets an inset above it.
  const bottomInset = zero && axis.min === 0 ? 0 : INSET;
  const y = linearScale(
    [axis.min, axis.max],
    [plot.y + plot.height - bottomInset, plot.y + INSET]
  );
  const start = colorIndex(style.color);
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      {legend ? (
        <SvgLegend
          keys={names.map((_, i) => ({
            color: paletteColor(start, i),
            kind: markers ? "line" : "swatch",
            shape: markerShape(i),
          }))}
          layout={legend}
          x={CHART_PADDING}
          y={frame.legendTop}
        />
      ) : null}
      <ValueAxis
        apa={style.apa}
        format={tick}
        orient="left"
        plot={plot}
        scale={y}
        ticks={axis.ticks}
      />
      {xAxis.draw}
      {names.map((name, series) => (
        <SeriesLine
          color={paletteColor(start, series)}
          fmt={fmt}
          key={`${series}-${name}`}
          markers={markers}
          points={rows.map((row, index) => {
            const value = row.values[series];
            return value === null ? null : [xAxis.position(index), y(value)];
          })}
          rows={rows}
          series={series}
          valueLabels={style.valueLabels}
        />
      ))}
    </ChartSvg>
  );
}
